import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import renderBubbles from './Bubbles';
import EmojiBubbles from './EmojiBubbles';
import WebFont from 'webfontloader';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { applyPreset } from './Presets';
import EmojiPicker from './EmojiPicker'; // Adjust the path based on your file structure

const Countdown = () => {
    const [endDate, setEndDate] = useState(moment().add(1, 'days').format('YYYY-MM-DDTHH:mm'));
    const [countdown, setCountdown] = useState('');
    const [selectedFont, setSelectedFont] = useState('Arial');
    const [fontSize, setFontSize] = useState(16);
    const [customText, setCustomText] = useState('Countdown:');
    const [finalText, setFinalText] = useState('Time is up!');
    const [textColor, setTextColor] = useState('#000000');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [isBold, setIsBold] = useState(false);
    const [selectedEffect, setSelectedEffect] = useState('none');
    const [marqueeSpeed, setMarqueeSpeed] = useState('medium');
    const [selectedTheme, setSelectedTheme] = useState('purple');
    const [swingPixels, setSwingPixels] = useState('1');
    const [useGradient, setUseGradient] = useState(false);
    const [gradientStartColor, setGradientStartColor] = useState('#ffffff');
    const [gradientEndColor, setGradientEndColor] = useState('#000000');
    const [useLineBreak, setUseLineBreak] = useState(false);
    const [bubbles, setBubbles] = useState([]);
    const [showBubbles, setShowBubbles] = useState(false);
    const [controlsOpen, setControlsOpen] = useState(false);
    const [bubblesType, setBubblesType] = useState('normal');
    const [selectedEmoji, setSelectedEmoji] = useState('🌟');
    const [shortLinkButtonText, setShortLinkButtonText] = useState('Create short link (beta)');
    const [url, setURL] = useState('');
    const [floatDirection, setFloatDirection] = useState('floatUp');
    const [floatSpeed, setFloatSpeed] = useState(5);
    const [maxEmojiSize, setMaxEmojiSize] = useState(40);
    const [useTextShadow, setUseTextShadow] = useState(false);

    const setStateFunctions = {
        endDate: setEndDate,
        customText: setCustomText,
        fontSize: setFontSize,
        selectedFont: setSelectedFont,
        textColor: setTextColor,
        backgroundColor: setBackgroundColor,
        isBold: setIsBold,
        marqueeSpeed: setMarqueeSpeed,
        selectedEffect: setSelectedEffect,
        swingPixels: setSwingPixels,
        selectedTheme: setSelectedTheme,
        useGradient: setUseGradient,
        gradientStartColor: setGradientStartColor,
        gradientEndColor: setGradientEndColor,
        useLineBreak: setUseLineBreak,
        showBubbles: setShowBubbles,
        finalText: setFinalText,
        bubblesType: setBubblesType,
        selectedEmoji: setSelectedEmoji,
        floatSpeed: setFloatSpeed,
        useTextShadow: setUseTextShadow
    };

    const handleApplyPreset = (presetKey) => {
        setBubblesType('normal');
        applyPreset(presetKey, setStateFunctions);
    };

    const themes = useMemo(() => ({
        purple: { textColor: '#ffffff', gradientStartColor: "#8d6595", gradientEndColor: "#3e0349" },
        red: { textColor: '#561515', gradientStartColor: "#c29393", gradientEndColor: "#e64141" },
        gold: { textColor: '#443a08', gradientStartColor: "#ecdf88", gradientEndColor: "#5c560f" },
        sunset: { textColor: '#695a26', gradientStartColor: "#f7e09a", gradientEndColor: "#d4a106" },
        steel: { textColor: '#ffffff', gradientStartColor: "#0F2027", gradientEndColor: "#2C5364" },
        pink: { textColor: '#000000', gradientStartColor: "#ee9ca7", gradientEndColor: "#ffdde1" },
        pepper: { textColor: '#ffffff', gradientStartColor: "#f12711", gradientEndColor: "#f5af19" },
        jungle: { textColor: '#ffffff', gradientStartColor: "#289a37", gradientEndColor: "#021707" },
        candy: { textColor: '#026E5C', gradientStartColor: "#30E8BF", gradientEndColor: "#FF8235" },
    }), []);

    const getMarqueeSpeed = (speed) => {
        switch (speed) {
            case 'snail':
                return selectedEffect === 'marquee' ? 200 : 10;
            case 'slowest':
                return selectedEffect === 'marquee' ? 60 : 2;
            case 'slow':
                return selectedEffect === 'marquee' ? 40 : 1.5;
            case 'medium':
                return selectedEffect === 'marquee' ? 20 : 1;
            case 'faster':
                return selectedEffect === 'marquee' ? 10 : 0.5;
            case 'fastest':
                return selectedEffect === 'marquee' ? 5 : 0.3;
            default:
                return selectedEffect === 'marquee' ? 20 : 1; // Default to medium speed
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const now = moment();
            const duration = moment.duration(moment(endDate).diff(now));

            const years = duration.years();
            const months = duration.months();
            const days = duration.days();
            const hours = duration.hours();
            const minutes = duration.minutes();
            const seconds = duration.seconds();

            // Initialize an empty array to hold the parts of the countdown string
            let countdownParts = [];

            // Only add years, months, and days to the countdown string if they are greater than 0
            if (years > 0) countdownParts.push(`${years} years`);
            if (months > 0) countdownParts.push(`${months} months`);
            if (days > 0) countdownParts.push(`${days} days`);

            // Always add hours, minutes, and seconds
            countdownParts.push(`${hours}h ${minutes}m ${seconds}s`);

            // Join the parts of the countdown string with spaces
            let countdownString = countdownParts.join(' ');

            setCountdown(countdownString);

            if (duration.asSeconds() <= 0) {
                clearInterval(interval);
                setCountdown(finalText);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [endDate, finalText]); // Include endDate in dependencies to update countdown when endDate changes

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const dateParam = queryParams.get('d');
        const fontParam = queryParams.get('f');
        const sizeParam = queryParams.get('s');
        const textParam = queryParams.get('t');
        const finalTextParam = queryParams.get('ft');
        const colorParam = queryParams.get('c');
        const bgcolorParam = queryParams.get('bc');
        const boldParam = queryParams.get('b');
        const speedParam = queryParams.get('sp'); // Add speedParam
        const effectParam = queryParams.get('e'); // Add effectParam
        const themeParam = queryParams.get('theme'); // Add themeParam
        const swingParam = queryParams.get('sw');
        const gradientParam = queryParams.get('g');
        const gradientstartParam = queryParams.get('gs');
        const gradientendParam = queryParams.get('ge');
        const linebreakParam = queryParams.get('lb');
        const showBubblesParam = queryParams.get('sb');
        const bubblesTypeParam = queryParams.get('bt');
        const selectedEmojiParam = queryParams.get('be');
        const floatDirectionParam = queryParams.get('fd');
        const floatSpeedParam = queryParams.get('fs');
        const maxEmojiSizeParam = queryParams.get('es');
        const useTextShadowParam = queryParams.get('uts');

        let intervalId;

        const applyTheme = (theme) => {
            setTextColor(theme.textColor);
            setUseGradient(true);
            setGradientStartColor(theme.gradientStartColor);
            setGradientEndColor(theme.gradientEndColor);
            setBackgroundColor('#000000');
            setFontSize(35);
        };

        if (!dateParam) {
            handleApplyPreset('default');
            setSelectedTheme('purple');
        }

        if (dateParam) {
            setEndDate(moment(dateParam, 'YYYY-MM-DDTHH:mm').format('YYYY-MM-DDTHH:mm'));
        }
        if (fontParam) {
            setSelectedFont(fontParam);
        }
        if (sizeParam) {
            setFontSize(parseInt(sizeParam, 10));
        }
        if (boldParam) {
            setIsBold(boldParam === 'true');
        }
        if (textParam) {
            setCustomText(textParam);
        }
        if (finalTextParam) {
            setFinalText(finalTextParam);
        }
        if (useTextShadowParam) {
            setUseTextShadow(useTextShadowParam === 'true');
        }
        if (linebreakParam) {
            setUseLineBreak(linebreakParam === 'true')
        }
        if (colorParam) {
            setTextColor(colorParam);
        }
        if (bgcolorParam) {
            setBackgroundColor(bgcolorParam);
        }
        if (gradientParam) {
            setUseGradient(gradientParam === 'true')
        }
        if (gradientstartParam) {
            setGradientStartColor(gradientstartParam)
        }
        if (gradientendParam) {
            setGradientEndColor(gradientendParam)
        }
        if (speedParam) {
            setMarqueeSpeed(speedParam);
        }
        if (swingParam) {
            setSwingPixels(swingParam);
        }
        if (effectParam) {
            setSelectedEffect(effectParam);
        }

        if (themeParam && themes[themeParam] && themeParam !== 'custom') {
            applyTheme(themes[themeParam]);
            setSelectedTheme(themeParam);
        }

        if (showBubblesParam) {
            setShowBubbles(showBubblesParam === 'true');
        }

        if (bubblesTypeParam) {
            setBubblesType(bubblesTypeParam);
        }

        if (selectedEmojiParam) {
            setSelectedEmoji(selectedEmojiParam);
        }

        if (floatDirectionParam) {
            setFloatDirection(floatDirectionParam);
        }

        if (floatSpeedParam) {
            setFloatSpeed(floatSpeedParam);
        }

        if (maxEmojiSizeParam) {
            setMaxEmojiSize(maxEmojiSizeParam);
        }

        return () => clearInterval(intervalId);
        // eslint-disable-next-line
    }, [themes]); // Only re-run effect if selected effect changes

    useEffect(() => {
        setBubbles(renderBubbles());
        WebFont.load({
            google: {
                families: [
                    'Pacifico',
                    'Barlow',
                    'Freckle+Face',
                    'Roboto',
                    'Open+Sans',
                    'Lato',
                    'Montserrat',
                    'Oswald',
                    'Raleway',
                    'Poppins',
                    'Merriweather',
                    'Caveat',
                    'Indie+Flower',
                    'Patrick+Hand'
                ],
            },
        });
    }, []);

    const toggleControls = () => {
        setControlsOpen(!controlsOpen);
    };

    const handleDateChange = (event) => {
        setEndDate(moment(event.target.value).format('YYYY-MM-DDTHH:mm'));
    };

    const handleFontChange = (event) => {
        setSelectedFont(event.target.value);
    };

    const handleFontSizeChange = (event) => {
        setFontSize(parseInt(event.target.value, 10));
    };

    const handleCustomTextChange = (event) => {
        setCustomText(event.target.value);
    };

    const handleFinalTextChange = (event) => {
        setFinalText(event.target.value);
    };

    const handleColorChange = (event) => {
        setTextColor(event.target.value);
    };

    const handleBgColorChange = (event) => {
        setBackgroundColor(event.target.value);
        setUseGradient(false);
    };

    const handleGradientStartColorChange = (event) => {
        setGradientStartColor(event.target.value);
    };

    const handleGradientEndColorChange = (event) => {
        setGradientEndColor(event.target.value);
    };

    const toggleBackgroundMode = () => {
        setGradientEndColor(backgroundColor)
        setUseGradient(!useGradient);
    };

    const handleEffectChange = (event) => {
        setSelectedEffect(event.target.value);
    };

    const handleMarqueeSpeedChange = (event) => {
        setMarqueeSpeed(event.target.value);
    };

    const handleSwingPixelsChange = (event) => {
        setSwingPixels(event.target.value)
    };

    const handleThemeChange = (event) => {
        const selectedTheme = event.target.value;
        setSelectedTheme(selectedTheme);

        if (themes[selectedTheme]) {
            const theme = themes[selectedTheme];
            setTextColor(theme.textColor);
            setUseGradient(true);
            setGradientStartColor(theme.gradientStartColor);
            setGradientEndColor(theme.gradientEndColor);
            setBackgroundColor('#000000');
            setSelectedFont('Arial');
            setFontSize(35);
        }
    };

    const countdownStyle = {
        fontFamily: selectedFont,
        fontSize: `${fontSize}px`,
        fontWeight: isBold ? 'bold' : 'normal',
        color: textColor,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80vh',
        textAlign: 'center',
        overflowX: 'hidden',
        whiteSpace: 'wrap',
        ...(useTextShadow && { textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' }),
    };

    const backgroundStyle = {};
    if (useGradient) {
        backgroundStyle.background = `linear-gradient(to bottom, ${gradientStartColor}, ${gradientEndColor})`;
    } else {
        backgroundStyle.background = backgroundColor;
    }

    const marqueeAnimation = `
    @keyframes marquee {
        0% {
            transform: translateX(100%);
        }
        50% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
`;


    const bounceAnimation = `
        @keyframes bounce {
            0%, 100% { transform: translateY(calc(${swingPixels}vh * 2)); }
            50% { transform: translateY(calc(-${swingPixels}vh * 2)); }
        }
    `;

    const crazyAnimation = `
        @keyframes crazy {
            0% {
                transform: translate(-5%, -5%) rotate(0deg);
            }
            20% {
                transform: translate(5%, 5%) rotate(360deg);
            }
            40% {
                transform: translate(-5%, 5%) rotate(-360deg);
            }
            60% {
                transform: translate(5%, -5%) rotate(360deg);
            }
            80% {
                transform: translate(5%, 5%) rotate(-360deg);
            }
            100% {
                transform: translate(-5%, -5%) rotate(0deg);
            }
        }
    `;

    const BananasAnimation = `
        @keyframes bananas {
            0% {
                transform: translate(0, 0) rotate(0deg);
                font-size: 0px;
            }
            20% {
                transform: translate(-25vw, -25vh) rotate(45deg);
                font-size: 25px;
            }
            40% {
                transform: translate(25vw, 25vh) rotate(180deg);
                font-size: 50px;
            }
            60% {
                transform: translate(-25vw, 25vh) rotate(270deg);
                font-size: 75px;
            }
            80% {
                transform: translate(25vw, -25vh) rotate(360deg);
                font-size: 100px;
            }
            100% {
                transform: translate(0, 0) rotate(0deg);
                font-size: 0px;
            }
        }
    `;


    const spiralZoomAnimation = `
        @keyframes spiralZoom {
            0% {
                transform: translate(0, 0) scale(1);
            }
            25% {
                transform: translateX(50px) rotate(90deg) scale(0.5);
            }
            50% {
                transform: translate(50px, 50px) rotate(180deg) scale(1.5);
            }
            75% {
                transform: translateY(50px) rotate(270deg) scale(0.75);
            }
            100% {
                transform: translate(0, 0) rotate(360deg) scale(1);
            }
        }
    `;

    const clockAnimation = `
        @keyframes clock {
            0%, 100% { transform: rotate(0deg); }
            8.333% { transform: rotate(30deg); }
            16.666% { transform: rotate(60deg); }
            25% { transform: rotate(90deg); }
            33.333% { transform: rotate(120deg); }
            41.666% { transform: rotate(150deg); }
            50% { transform: rotate(180deg); }
            58.333% { transform: rotate(210deg); }
            66.666% { transform: rotate(240deg); }
            75% { transform: rotate(270deg); }
            83.333% { transform: rotate(300deg); }
            91.666% { transform: rotate(330deg); }
        }
    `;

    const SqueezeAnimation = `
        @keyframes squeeze {
            0% {
                transform: scale(1);
            }
            50% {
                transform: scale(0.5, 0.8);
            }
            100% {
                transform: scale(1);
            }
        }
    `;

    const PulseAnimation = `
        @keyframes pulse {
            0% {
                transform: scale(1);
            }

            50% {
                transform: scale(2);
            }

            100% {
                transform: scale(1);
            }
        }
    `;

    const effectStyles = {
        marquee: { animation: `marquee ${getMarqueeSpeed(marqueeSpeed)}s infinite ease-in-out`, overflowX: 'hidden', overflowY: 'hidden' },
        bounce: { animation: `bounce ${getMarqueeSpeed(marqueeSpeed)}s infinite` },
        spiral: { animation: 'spiralZoom 20s infinite' },
        crazy: { animation: 'crazy 20s infinite', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' },
        bananas: { animation: 'bananas 10s infinite' },
        clock: { animation: 'clock 60s linear infinite' },
        squeeze: { animation: 'squeeze 1s ease 0s infinite normal forwards' },
        pulse: { animation: 'pulse 1s ease 0s infinite normal forwards' }
    };

    const bubbleAnimation = `
        @keyframes bubbles {
            0% {
                transform: translateY(100vh) scale(0.5); // Start below the screen
                opacity: 1;
            }
            100% {
                transform: translateY(-100vh) scale(1.2); // End above the screen
                opacity: 0;
            }
        }
    `;

    const handleShortenURL = async (url) => {
        try {
            const response = await fetch('https://shrt.soliman.nl/api/shorten', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
                cache: 'no-store',
            });
            const data = await response.json();

            if (response.ok) {
                return data.shortened_url;
            } else {
                alert(data.error);
            }
        } catch (error) {
            alert('An error occurred. Please try again.');
        }
    };

    const createShortLink = async () => {
        // Prod
        const base_url = "https://countdown.soliman.nl";

        // Dev
        // const base_url = "http://localhost:3000";

        const pars = "?d=" + encodeURIComponent(endDate) +
            "&t=" + encodeURIComponent(customText) +
            "&ft=" + encodeURIComponent(finalText) +
            "&s=" + fontSize +
            "&f=" + encodeURIComponent(selectedFont) +
            "&c=" + encodeURIComponent(textColor) +
            "&bc=" + encodeURIComponent(backgroundColor) +
            "&b=" + isBold +
            "&sp=" + marqueeSpeed +
            "&e=" + selectedEffect +
            "&sw=" + swingPixels +
            "&g=" + useGradient +
            "&gs=" + encodeURIComponent(gradientStartColor) +
            "&ge=" + encodeURIComponent(gradientEndColor) +
            "&lb=" + useLineBreak +
            "&sb=" + showBubbles +
            "&bt=" + bubblesType +
            "&be=" + selectedEmoji +
            "&fd=" + floatDirection +
            "&fs=" + floatSpeed +
            "&es=" + maxEmojiSize +
            "&uts=" + useTextShadow;

        const url = base_url + pars;

        setShortLinkButtonText('Creating a short link...');

        const short_url = await handleShortenURL(url);

        setURL(short_url);

        setShortLinkButtonText('Press again to copy link to clipboard');

    }

    const copyLinkToClipboard = () => {
        navigator.clipboard.writeText(url).then(() => {
            alert("Link copied to clipboard!");
            setShortLinkButtonText('Create short link (beta)');
            setURL('');
        }).catch(err => {
            console.error("Failed to copy link: ", err);
        });
    }

    const createLink = () => {
        // Prod
        const base_url = "https://countdown.soliman.nl";

        // Dev
        // const base_url = "http://localhost:3000";

        const pars = "?d=" + encodeURIComponent(endDate) +
            "&t=" + encodeURIComponent(customText) +
            "&ft=" + encodeURIComponent(finalText) +
            "&s=" + fontSize +
            "&f=" + encodeURIComponent(selectedFont) +
            "&c=" + encodeURIComponent(textColor) +
            "&bc=" + encodeURIComponent(backgroundColor) +
            "&b=" + isBold +
            "&sp=" + marqueeSpeed +
            "&e=" + selectedEffect +
            "&sw=" + swingPixels +
            "&g=" + useGradient +
            "&gs=" + encodeURIComponent(gradientStartColor) +
            "&ge=" + encodeURIComponent(gradientEndColor) +
            "&lb=" + useLineBreak +
            "&sb=" + showBubbles +
            "&bt=" + bubblesType +
            "&be=" + selectedEmoji +
            "&fd=" + floatDirection +
            "&fs=" + floatSpeed +
            "&es=" + maxEmojiSize +
            "&uts=" + useTextShadow;

        const url = base_url + pars;

        navigator.clipboard.writeText(url).then(() => {
            alert("Link copied to clipboard!");
        }).catch(err => {
            console.error("Failed to copy link: ", err);
        });
    }

    return (
        <>
            <div style={{ ...backgroundStyle, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                <div>
                    <style>{bubbleAnimation}</style>
                    {showBubbles && (
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                            {bubblesType === 'normal'
                                ? bubbles
                                : <EmojiBubbles
                                    key={bubblesType}
                                    emoji={selectedEmoji}
                                    floatDirection={floatDirection}
                                    floatSpeed={floatSpeed}
                                    numberOfBubbles={100}
                                    maxSize={maxEmojiSize}
                                />}
                        </div>
                    )}

                    <div style={{ ...backgroundStyle, height: '100vh', position: 'relative', zIndex: 0 }}>
                        {selectedEffect === 'marquee' && <style>{marqueeAnimation}</style>}
                        {selectedEffect === 'bounce' && <style>{bounceAnimation}</style>}
                        {selectedEffect === 'clock' && <style>{clockAnimation}</style>}
                        {selectedEffect === 'spiral' && <style>{spiralZoomAnimation}</style>}
                        {selectedEffect === 'crazy' && <style>{crazyAnimation}</style>}
                        {selectedEffect === 'bananas' && <style>{BananasAnimation}</style>}
                        {selectedEffect === 'squeeze' && <style>{SqueezeAnimation}</style>}
                        {selectedEffect === 'pulse' && <style>{PulseAnimation}</style>}
                        <div style={{ ...countdownStyle, ...(effectStyles[selectedEffect] || {}) }}>
                            {customText}
                            {useLineBreak ? (
                                <br />
                            ) : (
                                <>
                                    &nbsp;
                                </>
                            )}
                            {countdown}
                        </div>
                    </div>
                </div>
                <IconButton
                    onClick={toggleControls}
                    style={{ position: 'absolute', top: 10, left: 10, zIndex: 1200, padding: '12px' }}
                >
                    <MenuIcon />
                </IconButton>
            </div>
            <Drawer
                disableScrollLock
                anchor="left"
                open={controlsOpen}
                onClose={toggleControls}
                PaperProps={{
                    sx: { width: { xs: '90%', sm: '80%', md: '350px' }, padding: '16px', backgroundColor: '#f9f9f9', height: '100%', overflowY: 'auto' },
                }}

                ModalProps={{
                    BackdropProps: {
                        style: {
                            backgroundColor: 'transparent', // Disable the dimming effect
                        },
                    },
                }}
            >

                <div style={{ top: 0, padding: '10px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0 }}>Controls</h2>
                        <IconButton onClick={toggleControls}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <Typography variant="caption" component="div" sx={{ color: 'gray', marginTop: '5px' }}>
                        v2.4-beta
                    </Typography>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '5em' }}>

                    {/* Date */}
                    <TextField
                        label="Countdown to"
                        type="datetime-local"
                        value={endDate}
                        onChange={handleDateChange}
                        fullWidth
                        sx={{ marginTop: '5px' }}
                    />

                    {/* Text */}
                    <TextField
                        label="Text"
                        type="text"
                        value={customText}
                        onChange={handleCustomTextChange}
                        fullWidth
                        sx={{ marginTop: '5px' }}
                    />

                    {/* Text */}
                    <TextField
                        label="Final text"
                        type="text"
                        value={finalText}
                        onChange={handleFinalTextChange}
                        fullWidth
                        sx={{ marginTop: '5px' }}
                    />

                    <FormControlLabel
                        label="Enable Text Shadow"
                        control={
                            <Checkbox
                                checked={useTextShadow}
                                onChange={(event) => setUseTextShadow(event.target.checked)}
                            />
                        }
                    />

                    {/* Line break */}
                    <FormControlLabel
                        label="Add line break"
                        control={
                            <Checkbox
                                checked={useLineBreak}
                                onChange={(event) => setUseLineBreak(event.target.checked)}
                            />
                        }
                    />

                    {/* Color theme */}
                    <FormControl fullWidth>
                        <InputLabel id="color-theme-label">Color Theme</InputLabel>
                        <Select
                            labelId="color-theme-label"
                            value={selectedTheme}
                            onChange={handleThemeChange}
                            label="Color Theme"
                        >
                            <MenuItem value="purple">Purple</MenuItem>
                            <MenuItem value="red">Red</MenuItem>
                            <MenuItem value="gold">Gold</MenuItem>
                            <MenuItem value="sunset">Sunset</MenuItem>
                            <MenuItem value="steel">Steel</MenuItem>
                            <MenuItem value="pink">Pink</MenuItem>
                            <MenuItem value="pepper">Pepper</MenuItem>
                            <MenuItem value="jungle">Jungle</MenuItem>
                            <MenuItem value="candy">Candy</MenuItem>

                        </Select>
                    </FormControl>

                    {/* Font */}
                    <FormControl fullWidth>
                        <InputLabel id="font-select-label">Font</InputLabel>
                        <Select
                            labelId="font-select-label"
                            value={selectedFont}
                            onChange={handleFontChange}
                            label="Font"
                        >
                            <MenuItem value="Arial" style={{ fontFamily: "Arial" }}>Arial</MenuItem>
                            <MenuItem value="Book Antiqua" style={{ fontFamily: "Book Antiqua" }}>Book Antiqua</MenuItem>
                            <MenuItem value="Brush Script MT" style={{ fontFamily: "Brush Script MT" }}>Brush Script MT</MenuItem>
                            <MenuItem value="Century Gothic" style={{ fontFamily: "Century Gothic" }}>Century Gothic</MenuItem>
                            <MenuItem value="Comic Sans MS" style={{ fontFamily: "Comic Sans MS" }}>Comic Sans MS</MenuItem>
                            <MenuItem value="Courier" style={{ fontFamily: "Courier" }}>Courier</MenuItem>
                            <MenuItem value="Courier New" style={{ fontFamily: "Courier New" }}>Courier New</MenuItem>
                            <MenuItem value="Franklin Gothic Medium" style={{ fontFamily: "Franklin Gothic Medium" }}>Franklin Gothic Medium</MenuItem>
                            <MenuItem value="Garamond" style={{ fontFamily: "Garamond" }}>Garamond</MenuItem>
                            <MenuItem value="Georgia" style={{ fontFamily: "Georgia" }}>Georgia</MenuItem>
                            <MenuItem value="Helvetica" style={{ fontFamily: "Helvetica" }}>Helvetica</MenuItem>
                            <MenuItem value="Impact" style={{ fontFamily: "Impact" }}>Impact</MenuItem>
                            <MenuItem value="Lucida Sans Unicode" style={{ fontFamily: "Lucida Sans Unicode" }}>Lucida Sans Unicode</MenuItem>
                            <MenuItem value="Palatino" style={{ fontFamily: "Palatino" }}>Palatino</MenuItem>
                            <MenuItem value="Tahoma" style={{ fontFamily: "Tahoma" }}>Tahoma</MenuItem>
                            <MenuItem value="Times New Roman" style={{ fontFamily: "Times New Roman" }}>Times New Roman</MenuItem>
                            <MenuItem value="Trebuchet MS" style={{ fontFamily: "Trebuchet MS" }}>Trebuchet MS</MenuItem>
                            <MenuItem value="Verdana" style={{ fontFamily: "Verdana" }}>Verdana</MenuItem>
                            <MenuItem value="Pacifico" style={{ fontFamily: "Pacifico" }}>Pacifico</MenuItem>
                            <MenuItem value="Barlow" style={{ fontFamily: "Barlow" }}>Barlow</MenuItem>
                            <MenuItem value="Freckle Face" style={{ fontFamily: "Freckle Face" }}>Freckle Face</MenuItem>
                            <MenuItem value="Roboto" style={{ fontFamily: "Roboto" }}>Roboto</MenuItem>
                            <MenuItem value="Open Sans" style={{ fontFamily: "Open Sans" }}>Open Sans</MenuItem>
                            <MenuItem value="Lato" style={{ fontFamily: "Lato" }}>Lato</MenuItem>
                            <MenuItem value="Montserrat" style={{ fontFamily: "Montserrat" }}>Montserrat</MenuItem>
                            <MenuItem value="Oswald" style={{ fontFamily: "Oswald" }}>Oswald</MenuItem>
                            <MenuItem value="Raleway" style={{ fontFamily: "Raleway" }}>Raleway</MenuItem>
                            <MenuItem value="Poppins" style={{ fontFamily: "Poppins" }}>Poppins</MenuItem>
                            <MenuItem value="Merriweather" style={{ fontFamily: "Merriweather" }}>Merriweather</MenuItem>
                            <MenuItem value="Caveat" style={{ fontFamily: "Caveat" }}>Caveat</MenuItem>
                            <MenuItem value="Indie Flower" style={{ fontFamily: "Indie Flower" }}>Indie Flower</MenuItem>
                            <MenuItem value="Patrick Hand" style={{ fontFamily: "Patrick Hand" }}>Patrick Hand</MenuItem>

                        </Select>
                    </FormControl>

                    {/* Size */}
                    <TextField
                        label="Size"
                        type="number"
                        inputProps={{ min: 10, max: 500 }}
                        value={fontSize}
                        onChange={handleFontSizeChange}
                        fullWidth
                    />

                    {/* Bold */}
                    <FormControlLabel
                        label="Bold"
                        control={
                            <Checkbox
                                checked={isBold}
                                onChange={(event) => setIsBold(event.target.checked)}
                            />
                        }
                    />

                    {/* Text color */}
                    <TextField
                        label="Text Color"
                        type="color"
                        value={textColor}
                        onChange={handleColorChange}
                        fullWidth
                    />

                    {/* Switch to Gradient */}
                    <FormControlLabel
                        label="Switch to Gradient"
                        control={
                            <Checkbox
                                checked={useGradient}
                                onChange={toggleBackgroundMode}
                            />
                        }
                    />

                    {/* Gradient */}
                    {useGradient ? (
                        <>
                            <TextField
                                label="Gradient Start Color"
                                type="color"
                                value={gradientStartColor}
                                onChange={handleGradientStartColorChange}
                                fullWidth
                            />
                            <TextField
                                label="Gradient End Color"
                                type="color"
                                value={gradientEndColor}
                                onChange={handleGradientEndColorChange}
                                fullWidth
                            />
                        </>
                    ) : (
                        <TextField
                            label="Background Color"
                            type="color"
                            value={backgroundColor}
                            onChange={handleBgColorChange}
                            fullWidth
                        />
                    )}

                    {/* Effects */}
                    <FormControl fullWidth>
                        <InputLabel id="effect-label">Effect </InputLabel>
                        <Select
                            labelId="effect-label"
                            value={selectedEffect}
                            onChange={handleEffectChange}
                            label="Effect "
                        >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="marquee">Marquee</MenuItem>
                            <MenuItem value="bounce">Bouncing</MenuItem>
                            <MenuItem value="clock">Clock</MenuItem>
                            <MenuItem value="spiral">Spiral</MenuItem>
                            <MenuItem value="crazy">Crazy</MenuItem>
                            <MenuItem value="bananas">Bananas</MenuItem>
                            <MenuItem value="squeeze">Squeeze</MenuItem>
                            <MenuItem value="pulse">Pulse</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Speed and Swing (Conditionally Rendered) */}
                    {(selectedEffect === 'marquee' || selectedEffect === 'bounce') && (
                        <TextField
                            label="Speed"
                            select
                            value={marqueeSpeed}
                            onChange={handleMarqueeSpeedChange}
                            fullWidth
                        >
                            <MenuItem value="snail">Snail</MenuItem>
                            <MenuItem value="slowest">Slowest</MenuItem>
                            <MenuItem value="slow">Slow</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="faster">Faster</MenuItem>
                            <MenuItem value="fastest">Fastest</MenuItem>
                        </TextField>
                    )}

                    {selectedEffect === 'bounce' && (
                        <FormControl fullWidth>
                            <InputLabel id="swing-pixels-label">Swing </InputLabel>
                            <Select
                                labelId="swing-pixels-label"
                                value={swingPixels}
                                onChange={handleSwingPixelsChange}
                                label="Swing "
                            >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                                <MenuItem value="4">4</MenuItem>
                                <MenuItem value="5">5</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    {/* Bubbles */}
                    <FormControlLabel
                        label="Add bubbles [BETA!]"
                        control={
                            <Checkbox
                                checked={showBubbles}
                                onChange={(e) => setShowBubbles(e.target.checked)}
                            />
                        }
                    />

                    <FormControl fullWidth>
                        <InputLabel id="bubbles-type-label">Bubbles Type </InputLabel>
                        <Select
                            labelId="bubbles-type-label"
                            value={bubblesType}
                            onChange={(e) => setBubblesType(e.target.value)}
                            label="Bubbles type "
                        >
                            <MenuItem value="normal">Normal Bubbles</MenuItem>
                            <MenuItem value="emoji">Emoji Bubbles [EXPERIMENTAL!]</MenuItem>
                        </Select>
                    </FormControl>

                    {bubblesType === 'emoji' && (
                        <>
                            <FormControl fullWidth>
                                <EmojiPicker currentEmoji={selectedEmoji} onSelectEmoji={(emoji) => setSelectedEmoji(emoji)} />
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="float-direction-label">Float direction </InputLabel>
                                <Select
                                    labelId="float-direction-label"
                                    value={floatDirection}
                                    onChange={(e) => setFloatDirection(e.target.value)}
                                    label="Float direction "
                                >
                                    <MenuItem value="floatUp">Float up</MenuItem>
                                    <MenuItem value="floatDown">Float down</MenuItem>
                                    <MenuItem value="floatLeftToRight">Float left to right</MenuItem>
                                    <MenuItem value="floatRightToLeft">Float right to left</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="float-speed-label">Float speed </InputLabel>
                                <Select
                                    labelId="float-speed-label"
                                    value={floatSpeed}
                                    onChange={(e) => setFloatSpeed(e.target.value)}
                                    label="Float speed "
                                >
                                    <MenuItem value="1">1</MenuItem>
                                    <MenuItem value="2">2</MenuItem>
                                    <MenuItem value="3">3</MenuItem>
                                    <MenuItem value="4">4</MenuItem>
                                    <MenuItem value="5">5</MenuItem>
                                    <MenuItem value="6">6</MenuItem>
                                    <MenuItem value="7">7</MenuItem>
                                    <MenuItem value="8">8</MenuItem>
                                    <MenuItem value="9">9</MenuItem>
                                    <MenuItem value="10">10</MenuItem>

                                </Select>
                            </FormControl>
                            <TextField
                                label="Max emoji size"
                                type="number"
                                value={maxEmojiSize}
                                onChange={(e) => setMaxEmojiSize(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={createLink}
                        sx={{ backgroundColor: '#3e0349', color: '#ffffff' }}
                    >
                        Create link
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => (!url ? createShortLink() : copyLinkToClipboard())}
                        sx={{ backgroundColor: '#3e1320', color: '#ffffff' }}
                    >
                        {shortLinkButtonText}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open("https://donate.stripe.com/bIY28Y1pZ61z5Us6oo", "_blank", "noopener,noreferrer")}
                        sx={{
                            backgroundColor: '#FFD700', // Gold color for coffee theme
                            color: '#000000', // Black text
                            textTransform: 'none', // Prevents uppercase transformation
                            fontWeight: 'bold',
                        }}
                    >
                        ☕ Buy me a coffee
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open("https://atkfnmze4gr.typeform.com/to/HPWKDfUV", "_blank", "noopener,noreferrer")}
                        sx={{
                            backgroundColor: '#FFD700', // Gold color for coffee theme
                            color: '#000000', // Black text
                            textTransform: 'none', // Prevents uppercase transformation
                            fontWeight: 'bold',
                        }}
                    >
                        ✍️ Drop me a line
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={toggleControls}
                        sx={{ backgroundColor: '#3e1320', color: '#ffffff' }}
                    >
                        Close this menu
                    </Button>

                    <FormControl fullWidth>
                        <Typography variant='h6'>
                            Presets
                        </Typography>
                    </FormControl>

                    {/* Action Buttons */}
                    <Button
                        onClick={() => handleApplyPreset('zen')}
                        variant="contained"
                        sx={{ backgroundColor: '#3e0349', color: '#ffffff' }}
                    >
                        🧘‍♀️ Activate Zen Mode 🧘‍♂️
                    </Button>
                    <Button
                        onClick={() => handleApplyPreset('xmas')}
                        variant="contained"
                        sx={{ backgroundColor: '#e64141', color: '#ffffff' }}
                    >
                        🎄 Activate X-Mas Mode 🎄
                    </Button>
                    <Button
                        onClick={() => handleApplyPreset('newyear')}
                        variant="contained"
                        sx={{ backgroundColor: '#5c560f', color: '#ffffff' }}
                    >
                        🍾 Activate New Year Mode 🍾
                    </Button>
                    <Button
                        onClick={() => handleApplyPreset('beer')}
                        variant="contained"
                        sx={{ backgroundColor: '#695a26', color: '#ffffff' }}
                    >
                        🍺 Activate Beer Mode 🍺
                    </Button>

                </div>
            </Drawer>
        </>
    );
};

export default Countdown;
